<template>
  <div
    :class="$style.base"
    v-test="'stripe-checkout-flow-modal'"
  >
    <div
      v-if="plans.length > 1"
      :class="$style.header"
    >
      <div :class="$style.headerTop">
        <BaseHeading>
          {{ canUpgradeSubscription ? $t('subscription.steps.stripe_upgrade') : $t('subscription.steps.stripe_checkout') }}
        </BaseHeading>
        <BaseSwitch
          v-if="!isMarketLaunchCountry"
          v-model="selectedPeriod"
          :options="[
            {
              value: SubscriptionPeriod.Month,
              label: $t('subscription.payment.monthly')
            },
            {
              value: SubscriptionPeriod.Year,
              label: $t('subscription.payment.yearly'),
              info: {
                label: '-10%',
                color: 'green'
              }
            }
          ]"
          v-test="'subscription-frequency'"
        />
      </div>
      <BaseText
        v-if="isTrialExpired"
        iconBefore="logout"
        @click="onLogout"
        v-test="'stripe-subscription-logout'"
      >
        {{ $t('nav.logout') }}
      </BaseText>
    </div>
    <div v-if="loadingPrices">
      <BaseSpinner />
    </div>
    <ProLightOnly v-else-if="isProlightMarket" />
    <ProOnly v-else-if="isProOnly" />
    <div v-else>
      <StripeCheckoutPlan
        v-for="plan in plans"
        :key="plan.plan"
        :plan="plan"
        :disabled="
          currentPlan &&
          currentPlan.type === plan.plan.toLocaleLowerCase() &&
          stripeStatus === StripeSubscriptionStatus.Active
        "
        :highlighted="!isSubscribed && (plan.plan === SubscriptionPlan.Pro || plan.plan === SubscriptionPlan.Prolight)"
        :selected="plan.plan === selectedPlan"
        :showAllFeatures="showAllFeatures"
        @select="handlePlanSelected(plan.plan)"
      />
      <BaseText
        :iconAfter="showAllFeatures ? 'minus' : 'plus'"
        @click="toggleFeatures"
        v-test="'subscription-show-all-features'"
      >
        {{ $t(
            showAllFeatures
              ? 'subscription.show_main_features'
              : 'subscription.show_all_features'
          ) }}
      </BaseText>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { logout } from '@/helpers/session';
import StripeCheckoutPlan from '@/modules/dashboard/_shared/CheckoutPlan.vue';
import { useCompanyStore } from '@/stores/company';
import {
  StripeSubscriptionStatus,
  SubscriptionPeriod,
  SubscriptionPlan,
} from '@/types';
import { useSubscriptionStore } from '../store';
import { useStripeStore } from '../stripe/store';
import ProLightOnly from './ProLightOnly.vue';
import ProOnly from './ProOnly.vue';

const showAllFeatures = ref(false);

const { selectedPlan, plans, loadingPrices, selectedPeriod } = storeToRefs(
  useStripeStore(),
);
const subscriptionStore = useSubscriptionStore();
const {
  isSubscribed,
  isTrialExpired,
  stripeStatus,
  canUpgradeSubscription,
  isMarketLaunchCountry,
} = useCompanyStore();

subscriptionStore.getData();

const currentPlan = computed(() =>
  subscriptionStore.plansData.find((plan) => plan.current),
);
const handlePlanSelected = (plan: SubscriptionPlan) => {
  selectedPlan.value = plan;
  subscriptionStore.formData.plan = plan.toLowerCase();
};

const onLogout = () => {
  logout();
};

const toggleFeatures = () => (showAllFeatures.value = !showAllFeatures.value);

const isProOnly = computed(
  () =>
    plans.value.length === 1 && plans.value[0].plan === SubscriptionPlan.Pro,
);

const { isProlightMarket } = useCompanyStore();
</script>

<style
  lang="scss"
  module
>
.base {
  padding: $spacing * 1.5;

  &.smallScreen {
    padding: $spacing;
  }
}

.header {
  margin-bottom: $spacing * 2;
}

.headerTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: $spacing;
}
</style>
